import { useState } from 'react';
import './admin-scrimmage.styles.scss';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { LEAGUE_TYPES } from '@shared/leagueTypes.js';
import { chemistryGrade, lineupEdge } from '@shared/chemistry.js';
import { autoDepthChart } from '@shared/depthChart.js';
import { positions } from '@/utils/positions';
import DepthChartContainer from '@/components/SHARED/depth-chart/depth-chart-container.component';
import BoxScore from '@/components/SHARED/box-score/box-score.component';
import PlayByPlay from '@/components/SHARED/play-by-play/play-by-play.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { ModalAlert } from '@/components/modal/modal.component';

// Scrimmage lab: generate two teams from a league preset, adjust their depth
// charts, and simulate games between them. Nothing is saved.
const AdminScrimmage = () => {

    const [ type, setType ] = useState('college');
    const [ options, setOptions ] = useState({
        home: { prestige: 3, chemistry: '' },
        away: { prestige: 3, chemistry: '' },
    });
    const [ neutral, setNeutral ] = useState(false);
    const [ teams, setTeams ] = useState(null);
    const [ teamsType, setTeamsType ] = useState(null);
    const [ result, setResult ] = useState(null);
    const [ loading, setLoading ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);

    const settings = LEAGUE_TYPES[teamsType ?? type];

    function setOption(side, key, value) {
        setOptions({ ...options, [side]: { ...options[side], [key]: value } });
    }

    async function generateTeams() {
        setLoading('generate');
        const side = (s) => ({
            prestige: Number(options[s].prestige),
            ...(options[s].chemistry !== '' && { chemistry: Number(options[s].chemistry) }),
        });
        const res = await UseCloudFunction('generateScrimmageTeams', {
            type, home: side('home'), away: side('away'),
        });
        setLoading(null);
        if (res.error) {
            setAlertMessage(`Couldn't generate teams: ${res.error}`);
            return;
        }
        setTeams({ home: res.home, away: res.away });
        setTeamsType(res.type);
        setResult(null);
    }

    function setDepth(side, depth) {
        setTeams({ ...teams, [side]: { ...teams[side], depth } });
    }

    function autoDepth(side) {
        const team = teams[side];
        setDepth(side, autoDepthChart(team.players, team.chemistry, settings.chemistryStrength));
    }

    async function simGame() {
        setLoading('sim');
        const res = await UseCloudFunction('simScrimmage', {
            type: teamsType, home: teams.home, away: teams.away, neutral,
        });
        setLoading(null);
        if (res.error) {
            setAlertMessage(`Couldn't simulate the game: ${res.error}`);
            return;
        }
        setResult(res.result);
    }

    // Chemistry edge of the current starters, as the engine computes it
    // (admin-only detail; managers see only the roster grade).
    function starterEdge(team) {
        const chems = positions
            .map(pos => team.players[team.depth[`${pos.id}_0`]]?.chemistry)
            .filter(c => c !== undefined);
        const edge = lineupEdge(chems, team.chemistry);
        return `${edge >= 0 ? '+' : ''}${edge.toFixed(2)}`;
    }

    const periodsInRegulation = settings.minutesPerGame >= 48 ? 4 : 2;

    return (
        <div className='admin-scrimmage'>
            <div className='admin-scrimmage-content g-card'>
                <div className='card-title'>Scrimmage Lab</div>
                <p className='meta'>Generate two teams from a league preset, adjust their depth charts, and simulate games. Nothing is saved.</p>

                <div className='scrimmage-setup'>
                    <div className='field'>
                        <label htmlFor='scrim-type'>League type</label>
                        <select id='scrim-type' value={type} onChange={(e) => setType(e.target.value)}>
                            {Object.entries(LEAGUE_TYPES).map(([key, preset]) => (
                                <option key={key} value={key}>{preset.label}</option>
                            ))}
                        </select>
                    </div>
                    {['away', 'home'].map(side => (
                        <div key={side} className='scrimmage-side-options'>
                            <div className='side-label'>{side === 'home' ? 'Home team' : 'Away team'}</div>
                            {type === 'college' &&
                                <div className='field small'>
                                    <label htmlFor={`scrim-${side}-prestige`}>Prestige</label>
                                    <select id={`scrim-${side}-prestige`} value={options[side].prestige} onChange={(e) => setOption(side, 'prestige', e.target.value)}>
                                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            }
                            <div className='field small'>
                                <label htmlFor={`scrim-${side}-chem`}>Team chemistry</label>
                                <select id={`scrim-${side}-chem`} value={options[side].chemistry} onChange={(e) => setOption(side, 'chemistry', e.target.value)}>
                                    <option value=''>Random</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
                <div className='buttons'>
                    <button className={`g-button ${loading ? 'disabled' : ''}`} onClick={() => generateTeams()}>
                        {loading === 'generate' ? <ThreeDotsLoader /> : (teams ? 'Generate New Teams' : 'Generate Teams')}
                    </button>
                </div>

                {teams &&
                    <>
                        <hr />
                        <div className='scrimmage-teams'>
                            {['away', 'home'].map(side => {
                                const team = teams[side];
                                return (
                                    <div key={side} className='scrimmage-team'>
                                        <div className='team-head'>
                                            <h4>{team.name}</h4>
                                            <button className='g-button small-btn' onClick={() => autoDepth(side)}>Auto Depth Chart</button>
                                        </div>
                                        <div className='team-stats'>
                                            Chemistry grade: <b>{chemistryGrade(Object.values(team.players), team.chemistry).grade}</b>
                                            <span className='meta'> (what the manager sees)</span>
                                        </div>
                                        <div className='team-stats meta'>
                                            Admin only: team chemistry {team.chemistry} · starting lineup edge {starterEdge(team)}
                                        </div>
                                        <DepthChartContainer
                                            players={team.players}
                                            depth={team.depth}
                                            changeDepth={(depth) => setDepth(side, depth)}
                                            showHidden={true} />
                                    </div>
                                )
                            })}
                        </div>
                        <div className='g-space-1'></div>
                        <div className='buttons'>
                            <div className='field-checkbox'>
                                <input type='checkbox' id='scrim-neutral' checked={neutral} onChange={(e) => setNeutral(e.target.checked)} />
                                <label htmlFor='scrim-neutral'>Neutral site</label>
                            </div>
                            <button className={`g-button primary ${loading ? 'disabled' : ''}`} onClick={() => simGame()}>
                                {loading === 'sim' ? <ThreeDotsLoader /> : (result ? 'Simulate Again' : 'Simulate Game')}
                            </button>
                        </div>
                    </>
                }

                {result &&
                    <>
                        <hr />
                        <div className='scrimmage-final'>
                            <span>{result.away.name} {result.away.score}</span>
                            <span className='final-label'>Final{result.overtimes ? ` (${result.overtimes === 1 ? 'OT' : `${result.overtimes}OT`})` : ''}</span>
                            <span>{result.home.score} {result.home.name}</span>
                        </div>
                        {result.injuries.length > 0 &&
                            <p className='meta'>
                                Injuries: {result.injuries.map(i => {
                                    const p = teams[i.teamId]?.players[i.playerId];
                                    return `${p ? `${p.fName} ${p.lName}` : i.playerId} (${i.gamesOut} game${i.gamesOut > 1 ? 's' : ''})`;
                                }).join(', ')}
                            </p>
                        }
                        <BoxScore teamResult={result.away} players={teams.away.players} />
                        <div className='g-space-1'></div>
                        <BoxScore teamResult={result.home} players={teams.home.players} />
                        <div className='g-space-1'></div>
                        <h4>Play-by-play</h4>
                        <PlayByPlay
                            pbp={result.pbp}
                            homeName={result.home.name}
                            awayName={result.away.name}
                            periodsInRegulation={periodsInRegulation} />
                        <p className='meta'>Game seed: {String(result.seed)}</p>
                    </>
                }
            </div>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default AdminScrimmage
